import { fetchNetlifyAuthToken } from "./netlifyAuthToken";

// Define a constant for the cache duration
const CACHE_DURATION_MS = 86400000; // 1 day

export const getCustomer = async (t) => {
  const organization = await t.organization("id");
  const organizationId = organization.id;
  const localStorageKey = `${organizationId}-customerData`;

  try {
    // Check localStorage for cached data
    const cachedDataString = localStorage.getItem(localStorageKey);
    const cachedData = cachedDataString ? JSON.parse(cachedDataString) : null;
    const currentTime = new Date().getTime();

    // Check if data is available in localStorage and is not older than the cache duration
    if (
      cachedData &&
      currentTime - cachedData.timestamp < CACHE_DURATION_MS &&
      !cachedData.updateLocalStorageData
    ) {
      return cachedData.data;
    }

    // Fetch from server if not in localStorage or outdated
    if (process.env.POWERUP_NAME.includes("LOCAL")) {
      // Use Trello's storage for local development
      const localCustomerData = await t.get(
        "organization",
        "shared",
        "localCustomerData"
      );

      // If no data exists, then create early adopter customer and return the data
      if (!localCustomerData) {
        return await createEarlyAdopter(t);
      }

      // Store fetched data and timestamp in localStorage
      const dataToStore = {
        data: localCustomerData,
        timestamp: currentTime,
        updateLocalStorageData: false,
      };
      localStorage.setItem(localStorageKey, JSON.stringify(dataToStore));
      return localCustomerData;
    } else {
      // use Netlify blob storage for non-local development
      const token = await fetchNetlifyAuthToken();
      const response = await fetch(
        `/.netlify/functions/customerApi?organizationId=${organizationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch customer data");
      }

      const customerDataString = await response.json();

      // If no data exists, then create early adopter customer and return the data
      if (response.ok && !customerDataString) {
        return await createEarlyAdopter(t);
      }

      const customerDataObject = JSON.parse(customerDataString);

      // Store fetched data and timestamp in localStorage
      const dataToStore = {
        data: customerDataObject,
        timestamp: currentTime,
        updateLocalStorageData: false,
      };
      localStorage.setItem(localStorageKey, JSON.stringify(dataToStore));
      return customerDataObject;
    }
  } catch (error) {
    console.error("Error fetching customer data in getCustomer:", error);
    throw new Error("Failed to retrieve customer data.");
  }
};

export const updateCustomer = async ({
  t,
  createdDate,
  earlyAdopter,
  stripeCustomerId,
  stripeSubscriptionId,
  stripeSubscriptionStatus,
}) => {
  try {
    const organization = await t.organization("id", "name");
    const organizationId = organization.id;
    const organizationName = organization.name;

    // Fetch existing customer data
    const existingData = earlyAdopter ? {} : await getCustomer(t);

    // Merge existing data with new data
    const customerData = {
      organizationId,
      data: {
        organizationName,
        createdDate: createdDate || existingData.createdDate || null,
        earlyAdopter:
          earlyAdopter !== undefined ? earlyAdopter : existingData.earlyAdopter,
        stripeCustomerId:
          stripeCustomerId || existingData.stripeCustomerId || null,
        stripeSubscriptionId:
          stripeSubscriptionId || existingData.stripeSubscriptionId || null,
        stripeSubscriptionStatus:
          stripeSubscriptionStatus ||
          existingData.stripeSubscriptionStatus ||
          null,
      },
    };

    const localCustomerData = {
      organizationId,
      ...customerData.data,
    };

    if (process.env.POWERUP_NAME.includes("LOCAL")) {
      // Use Trello's storage for local development
      await t.set(
        "organization",
        "shared",
        "localCustomerData",
        localCustomerData
      );
    } else {
      // Use server for non-local environments
      const token = await fetchNetlifyAuthToken();
      const response = await fetch("/.netlify/functions/customerApi", {
        method: "POST",
        body: JSON.stringify(customerData),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to update customer data");
      }
    }

    // Store data and timestamp in localStorage
    const localStorageKey = `${organizationId}-customerData`;
    const currentTime = new Date().getTime();
    const dataToStore = {
      data: localCustomerData,
      timestamp: currentTime,
      updateLocalStorageData: false,
    };
    localStorage.setItem(localStorageKey, JSON.stringify(dataToStore));
  } catch (error) {
    console.error("Error updating customer data:", error);
    throw new Error("Failed to update customer data.");
  }
};

export const updateLocalStorageData = async (t) => {
  const organization = await t.organization("id");
  const organizationId = organization.id;

  // Set the flag to fetch and update local storage
  const localStorageKey = `${organizationId}-customerData`;
  const cachedDataString = localStorage.getItem(localStorageKey);
  const cachedData = cachedDataString ? JSON.parse(cachedDataString) : {};
  cachedData.updateLocalStorageData = true;
  localStorage.setItem(localStorageKey, JSON.stringify(cachedData));
};

export const updateCustomerDataOnEnable = async (t) => {
  const organization = await t.organization("id");
  const organizationId = organization.id;
  const createdDate = new Date().toISOString();

  try {
    // Use Trello's storage for local development
    if (process.env.POWERUP_NAME.includes("LOCAL")) {
      const localCustomerData = await t.get(
        "organization",
        "shared",
        "localCustomerData"
      );

      // If no data exists, then create new customer
      if (!localCustomerData) {
        await updateCustomer({
          t,
          createdDate,
          earlyAdopter: false,
          stripeCustomerId: null,
          stripeSubscriptionId: null,
          stripeSubscriptionStatus: null,
        });
      }
    } else {
      // use Netlify blob storage for non-local development
      const token = await fetchNetlifyAuthToken();
      const response = await fetch(
        `/.netlify/functions/customerApi?organizationId=${organizationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch customer data");
      }

      const customerData = await response.json();

      // If no data exists, then create new customer
      if (response.ok && !customerData) {
        await updateCustomer({
          t,
          createdDate,
          earlyAdopter: false,
          stripeCustomerId: null,
          stripeSubscriptionId: null,
          stripeSubscriptionStatus: null,
        });
      }
    }
  } catch (error) {
    console.error("Error updating customer data on enable:", error);
    throw error;
  }
};

export async function createEarlyAdopter(t) {
  const createdDate = new Date().toISOString();

  const customerData = {
    t,
    createdDate,
    earlyAdopter: true,
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    stripeSubscriptionStatus: null,
  };

  await updateCustomer(customerData);
  return customerData;
}

export async function openSubscribeModal(t) {
  return t.modal({
    title: "Card Relationships",
    accentColor: "#0055cc",
    url: "./subscribe.html",
  });
}

export const openStripeCustomerPortal = async (stripeCustomerId) => {
  try {
    const response = await fetch(
      "/.netlify/functions/create-customer-portal-session",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stripeCustomerId }),
      }
    );

    const data = await response.json();
    if (response.ok) {
      window.open(data.url, "_blank");
    } else {
      console.error("Error creating customer portal session:", data.error);
    }
  } catch (error) {
    console.error("Error opening Stripe customer portal:", error);
  }
};
